/**
 * @voltxampere/web — Solder shimmer state (Sprint 58b)
 *
 * Per-joint sparkle timing + global concurrent cap (≤5 active
 * sparkles per Sprint 58b pivot 4 guidance — avoids visual
 * clutter on dense fixtures).
 *
 * Joints are inferred from the circuit: every component terminal
 * with a non-null netId is a joint candidate. The store keeps
 * per-joint `nextSparkleAt` timestamps (Poisson-style random
 * draw inside the [SPARKLE_INTERVAL_MIN_MS, ..MAX_MS] window) so
 * the visual reads as quietly twinkly rather than periodic.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { computed, effect, signal, type ReadonlySignal } from "@preact/signals-core";
import type { ComponentInstance, Point } from "@voltxampere/schema";

import { currentCircuit } from "./circuit.js";

export const SPARKLE_INTERVAL_MIN_MS = 5_000;
export const SPARKLE_INTERVAL_MAX_MS = 15_000;
export const SPARKLE_DURATION_MS = 320;
export const MAX_CONCURRENT_SPARKLES = 5;

export interface JointDescriptor {
  readonly componentId: string;
  readonly terminalId: string;
  readonly worldPoint: Point;
}

/** Active sparkles — `{ jointKey, startedAtMs }`. Renderer reads. */
export interface SparkleState {
  readonly jointKey: string;
  readonly worldPoint: Point;
  readonly startedAtMs: number;
}

export const activeSparkles = signal<readonly SparkleState[]>([]);

// Internal — per-joint next-sparkle scheduling.
const nextSparkleAt = new Map<string, number>();

/**
 * Derived: every joint coord in the current circuit. Re-computed
 * whenever `currentCircuit` changes (rarely — circuit topology is
 * stable once loaded). Joint key = `${componentId}::${terminalId}`.
 */
export const joints: ReadonlySignal<readonly JointDescriptor[]> = computed(() => {
  const circuit = currentCircuit.value;
  if (circuit === null) return [];
  const out: JointDescriptor[] = [];
  for (const c of circuit.components) {
    appendComponentJoints(c, out);
  }
  return out;
});

function appendComponentJoints(c: ComponentInstance, out: JointDescriptor[]): void {
  for (const t of c.terminals) {
    out.push({
      componentId: c.id,
      terminalId: t.terminalId,
      // Terminal position is in body-local coords; the canvas
      // renderer already exposes pin world coords elsewhere
      // (Sprint 25 R2 lane 4D-A `_layout-helpers`). For S58b the
      // approximate world position = component.position + terminal
      // position is sufficient — the renderer maps to screen coords
      // before drawing.
      worldPoint: {
        x: c.position.x + t.position.x,
        y: c.position.y + t.position.y,
      },
    });
  }
}

export function jointKey(d: { readonly componentId: string; readonly terminalId: string }): string {
  return `${d.componentId}::${d.terminalId}`;
}

/**
 * Tick — called per rAF frame. Walks joints, fires new sparkles
 * subject to the concurrent cap, and evicts expired sparkles.
 *
 * Pure store mutation; renderer reads `activeSparkles`. Caller is
 * responsible for viewport culling on render — store does not
 * track viewport state to keep the dependency direction one-way
 * (canvas → store, never store → DOM).
 */
export function tickShimmer(nowMs: number, rng: () => number = Math.random): void {
  const list = joints.value;
  // Evict expired.
  const survivors: SparkleState[] = [];
  for (const s of activeSparkles.value) {
    if (nowMs - s.startedAtMs < SPARKLE_DURATION_MS) survivors.push(s);
  }

  // Fire new ones, respecting the concurrent cap.
  const liveKeys = new Set(survivors.map((s) => s.jointKey));
  for (const j of list) {
    const key = jointKey(j);
    const next = nextSparkleAt.get(key);
    if (next === undefined) {
      // First sight — schedule the initial sparkle.
      nextSparkleAt.set(key, nowMs + sampleInterval(rng));
      continue;
    }
    if (nowMs >= next && !liveKeys.has(key) && survivors.length < MAX_CONCURRENT_SPARKLES) {
      survivors.push({ jointKey: key, worldPoint: j.worldPoint, startedAtMs: nowMs });
      liveKeys.add(key);
      nextSparkleAt.set(key, nowMs + sampleInterval(rng));
    }
  }
  if (
    survivors.length !== activeSparkles.value.length ||
    survivors.some((s, i) => activeSparkles.value[i]?.jointKey !== s.jointKey)
  ) {
    activeSparkles.value = survivors;
  }
}

function sampleInterval(rng: () => number): number {
  return SPARKLE_INTERVAL_MIN_MS + rng() * (SPARKLE_INTERVAL_MAX_MS - SPARKLE_INTERVAL_MIN_MS);
}

let activeUnsubscribe: (() => void) | null = null;

/**
 * Reset per-joint scheduling whenever the circuit identity changes
 * so old joint keys don't leak between fixtures (preset switch,
 * fresh boot, etc).
 */
export function bootSolderShimmer(): () => void {
  if (activeUnsubscribe) return activeUnsubscribe;
  let lastId: string | null = null;
  const dispose = effect(() => {
    const c = currentCircuit.value;
    const id = c?.id ?? null;
    if (id !== lastId) {
      lastId = id;
      nextSparkleAt.clear();
      activeSparkles.value = [];
    }
  });
  activeUnsubscribe = (): void => {
    dispose();
    activeUnsubscribe = null;
  };
  return activeUnsubscribe;
}

/** Test helper — full reset. */
export function resetSolderShimmerForTests(): void {
  activeUnsubscribe?.();
  activeUnsubscribe = null;
  activeSparkles.value = [];
  nextSparkleAt.clear();
}
